import { CellWithTooltip } from "@/components/data-table/CellWithTooltip";
import { ColumnHeader } from "@/components/data-table/ColumnHeader";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { AppSessionsTableType } from "../types/appSession.type";

export const columns: ColumnDef<AppSessionsTableType>[] = [
  {
    accessorKey: "appName",
    header: ({ column }) => <ColumnHeader column={column} title="App" />,
    cell: ({ row }) => <CellWithTooltip cellValue={row.getValue("appName")} />,
    enableHiding: false
  },
  {
    accessorKey: "userId",
    header: ({ column }) => <ColumnHeader column={column} title="User ID" />,
    cell: ({ row }) => (
      <CellWithTooltip
        cellClassName="w-[80px] truncate"
        cellValue={row.getValue("userId")}
      />
    ),
    enableHiding: false
  },
  {
    accessorKey: "userEmail",
    header: ({ column }) => <ColumnHeader column={column} title="User Email" />,
    cell: ({ row }) => (
      <CellWithTooltip cellValue={row.getValue("userEmail")} />
    ),
    enableHiding: false
  },
  {
    accessorKey: "ownerOrganization",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Owner organization" />
    ),
    cell: ({ row }) => (
      <CellWithTooltip cellValue={row.getValue("ownerOrganization")} />
    ),
    enableHiding: false
  },
  {
    accessorKey: "accessType",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Access type" />
    ),
    cell: ({ row }) => (
      <CellWithTooltip
        cellValue={row.getValue("accessType")}
        cellClassName="lowercase first-letter:uppercase"
      />
    ),
    enableHiding: false
  },
  {
    accessorKey: "startTime",
    header: ({ column }) => <ColumnHeader column={column} title="Start time" />,
    cell: ({ row }) =>
      row.original.startTime && !row.getIsGrouped()
        ? format(row.getValue("startTime"), "MMM d, y, HH:mm:ss")
        : null,
    aggregatedCell: () => null,
    enableHiding: false,
    enableGrouping: false
  },
  {
    accessorKey: "endTime",
    header: ({ column }) => <ColumnHeader column={column} title="End time" />,
    cell: ({ row }) =>
      row.original.endTime && !row.getIsGrouped()
        ? format(row.getValue("endTime"), "MMM d, y, HH:mm:ss")
        : null,
    aggregatedCell: () => null,
    enableHiding: false,
    enableGrouping: false
  },
  {
    id: "sessionDuration",
    accessorFn: (o) => {
      return o.startTime && o.endTime
        ? Math.round((o.endTime.getTime() - o.startTime.getTime()) / 1000)
        : null;
    },
    header: ({ column }) => (
      <ColumnHeader column={column} title="Session duration" />
    ),
    cell: ({ row }) => formatDurationString(row.getValue("sessionDuration")),
    aggregationFn: "mean",
    aggregatedCell: ({ getValue }) => formatDurationString(getValue<number>()),
    enableHiding: false,
    enableGrouping: false
  }
];

const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

function formatDurationString(durationInSeconds: number) {
  let duration = Math.round(durationInSeconds);

  const days = Math.floor(duration / SECONDS_PER_DAY);
  if (days > 0) duration -= SECONDS_PER_DAY * days;

  const hours = Math.floor(duration / SECONDS_PER_HOUR);
  if (hours > 0) duration -= SECONDS_PER_HOUR * hours;

  const minutes = Math.floor(duration / SECONDS_PER_MINUTE);
  if (minutes > 0) duration -= SECONDS_PER_MINUTE * minutes;

  let formattedDuration = "";

  if (days > 0) formattedDuration += `${days}d `;
  if (hours > 0) formattedDuration += `${hours}h `;
  if (minutes > 0) formattedDuration += `${minutes}m `;
  if (duration > 0) formattedDuration += `${duration}s`;

  return formattedDuration;
}
